// src/components/DatabaseExplorer/DynamicDatabaseExplorer.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";

import axios from "axios";
import { FiDatabase, FiAlertCircle } from "react-icons/fi";

import DatabaseSelector from "./DatabaseSelector";
import ExplorerToolbar from "./ExplorerToolbar";
import DynamicFilters from "./DynamicFilters";
import DynamicDataTable from "./DynamicDataTable";
import TablePagination from "./TablePagination";
import RowDetailsModal from "./RowDetailsModal";

import { API_CONFIG, DATABASE_TABLES } from "./databaseExplorerConfig";

import {
  getColumnName,
  getFilterType,
  extractColumns,
  extractRows,
  getErrorMessage,
} from "./databaseExplorerUtils";

import { getCurrentDate, getCurrentMonthFirstDate } from "../Utilitie";
import DownloadExcelFromData from "../Extra/DownloadExcelFromData";

/* =========================================================
   Helpers
========================================================= */

function filterRows(rows, columns, search, filters) {
  const query = search.trim().toLowerCase();
  return rows.filter((row) => {
    /* -----------------------------------------------------
       Global search
    ----------------------------------------------------- */
    if (
      query &&
      !columns.some((column) => {
        const value = row[getColumnName(column)];

        if (value === null || value === undefined) {
          return false;
        }

        const searchableValue =
          typeof value === "object" ? JSON.stringify(value) : String(value);

        return searchableValue.toLowerCase().includes(query);
      })
    ) {
      return false;
    }

    /* -----------------------------------------------------
       Column filters
    ----------------------------------------------------- */
    return columns.every((column) => {
      const name = getColumnName(column);
      const filter = filters[name];

      if (!filter) {
        return true;
      }

      const raw = row[name];
      const type = getFilterType(column);

      /* ---------------------------------------------------
         Number
      --------------------------------------------------- */
      if (type === "number") {
        if (filter.min !== "" && filter.min != null) {
          if (raw == null || Number(raw) < Number(filter.min)) {
            return false;
          }
        }

        if (filter.max !== "" && filter.max != null) {
          if (raw == null || Number(raw) > Number(filter.max)) {
            return false;
          }
        }

        return true;
      }

      /* ---------------------------------------------------
         Date / DateTime
      --------------------------------------------------- */
      if (type === "date" || type === "datetime") {
        const value = raw == null ? "" : String(raw).slice(0, 10);

        if (filter.from && (!value || value < filter.from)) {
          return false;
        }

        if (filter.to && (!value || value > filter.to)) {
          return false;
        }

        return true;
      }

      /* ---------------------------------------------------
         Boolean
      --------------------------------------------------- */
      if (type === "boolean") {
        if (filter.equals === "" || filter.equals == null) {
          return true;
        }

        const actual =
          raw === true || raw === 1 || raw === "1" || raw === "true"
            ? "1"
            : raw === false || raw === 0 || raw === "0" || raw === "false"
              ? "0"
              : String(raw);

        return actual === filter.equals;
      }

      /* ---------------------------------------------------
         Enum
      --------------------------------------------------- */
      if (type === "enum") {
        return (
          filter.equals === "" ||
          filter.equals == null ||
          String(raw.toLowerCase() ?? "") === filter.equals
        );
      }

      /* ---------------------------------------------------
         Text
      --------------------------------------------------- */
      const operator = filter.operator || "contains";

      const value = String(raw ?? "").toLowerCase();

      const text = String(filter.value ?? "").toLowerCase();

      if (operator === "empty") {
        return raw == null || String(raw).trim() === "";
      }

      if (operator === "notEmpty") {
        return raw != null && String(raw).trim() !== "";
      }

      if (!text) {
        return true;
      }

      if (operator === "equals") {
        return value === text;
      }

      if (operator === "starts") {
        return value.startsWith(text);
      }

      return value.includes(text);
    });
  });
}

/* =========================================================
   Sorting
========================================================= */

function sortRows(rows, columns, sortConfig) {
  if (!sortConfig.key) {
    return rows;
  }

  const column = columns.find((item) => getColumnName(item) === sortConfig.key);
  const type = column ? getFilterType(column) : "text";
  const direction = sortConfig.direction === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const first = a[sortConfig.key];
    const second = b[sortConfig.key];

    if (first == null && second == null) {
      return 0;
    }

    if (first == null) {
      return 1;
    }

    if (second == null) {
      return -1;
    }

    let comparison = 0;

    /* -----------------------------------------------------
       Number
    ----------------------------------------------------- */
    if (type === "number") {
      const aNumber = Number(first);
      const bNumber = Number(second);

      comparison =
        Number.isNaN(aNumber) || Number.isNaN(bNumber)
          ? String(first).localeCompare(String(second))
          : aNumber - bNumber;
    } else {
      /* ---------------------------------------------------
         Object-safe sorting
      --------------------------------------------------- */
      const firstValue =
        typeof first === "object" ? JSON.stringify(first) : String(first);

      const secondValue =
        typeof second === "object" ? JSON.stringify(second) : String(second);

      comparison = firstValue.localeCompare(secondValue, undefined, {
        numeric: true,
        sensitivity: "base",
      });
    }

    return comparison * direction;
  });
}

/* =========================================================
   Component
========================================================= */

export default function DynamicDatabaseExplorer({
  databaseTables = DATABASE_TABLES,
  initialDatabase = "",
  initialTable = "",
  pageSizeOptions = [25, 50, 100],
}) {
  /* =======================================================
     Databases
  ======================================================= */

  const databases = useMemo(
    () => Object.keys(databaseTables),
    [databaseTables],
  );

  const startingDatabase =
    initialDatabase && databases.includes(initialDatabase)
      ? initialDatabase
      : databases[0] || "";

  const [database, setDatabase] = useState(startingDatabase);

  /* =======================================================
     Tables

     IMPORTANT:
     tables are objects:

     {
       table: "t_interchange",
       date_column: "date"
     }
  ======================================================= */

  const tables = databaseTables[database] || [];

  const startingTable =
    initialTable && tables.some((item) => item?.table === initialTable)
      ? tables.find((item) => item?.table === initialTable)
      : tables[0] || null;

  const [table, setTable] = useState(startingTable);

  /* =======================================================
     State
  ======================================================= */

  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [date, setDate] = useState({
    from: getCurrentMonthFirstDate(),
    to: getCurrentDate(),
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: "",
    direction: "asc",
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selectedRow, setSelectedRow] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  /* =======================================================
     Fetch table
  ======================================================= */

  const fetchTable = useCallback(
    async (signal) => {
      /* ---------------------------------------------------
         Validate selection
      --------------------------------------------------- */

      if (
        !database ||
        !table?.table ||
        !Object.prototype.hasOwnProperty.call(databaseTables, database)
      ) {
        setColumns([]);
        setRows([]);
        setError("Select an allowed database and table.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");

      try {
        const params = {
          ...API_CONFIG.extraParams,

          [API_CONFIG.databaseParam]: database,

          [API_CONFIG.tableParam]: table,
        };

        /* -------------------------------------------------
           API calls
        ------------------------------------------------- */

        const [describeResponse, fetchResponse] = await Promise.all([
          axios.get(
            `${API_CONFIG.describeUrl}${params.table.table}?database_name=${params.database}`,
            {
              signal,
            },
          ),

          axios.get(
            `${API_CONFIG.fetchUrl}${params.table.table}` +
              `&date_column=${params.table.date_column}` +
              `&from_date=${date.from}` +
              `&to_date=${date.to}` +
              `&database_name=${params.database}`,
            {
              signal,
            },
          ),
        ]);

        /* -------------------------------------------------
           API error
        ------------------------------------------------- */

        if (
          describeResponse.data?.success === false ||
          fetchResponse.data?.success === false
        ) {
          throw new Error(
            describeResponse.data?.message ||
              fetchResponse.data?.message ||
              "The server could not load this table.",
          );
        }

        /* -------------------------------------------------
           Extract data
        ------------------------------------------------- */

        const nextColumns = extractColumns(describeResponse);

        const nextRows = extractRows(fetchResponse);

        /* -------------------------------------------------
           Validate columns
        ------------------------------------------------- */

        if (!nextColumns.length) {
          throw new Error("The describe API did not return a columns array.");
        }

        /* -------------------------------------------------
           Validate rows
        ------------------------------------------------- */

        if (
          nextRows.length > 0 &&
          nextRows.some(
            (row) => !row || typeof row !== "object" || Array.isArray(row),
          )
        ) {
          throw new Error("The fetch API returned an invalid row format.");
        }

        /* -------------------------------------------------
           Update state
        ------------------------------------------------- */

        setColumns(nextColumns);
        setRows(nextRows);

        setFilters({});
        setSearch("");

        setSortConfig({
          key: "",
          direction: "asc",
        });

        setPage(1);
        setSelectedRow(null);
      } catch (requestError) {
        if (requestError?.code === "ERR_CANCELED") {
          return;
        }

        setColumns([]);
        setRows([]);

        setError(getErrorMessage(requestError));
      } finally {
        if (!signal?.aborted) {
          setLoading(false);
        }
      }
    },
    [database, table, databaseTables, date.from, date.to],
  );

  /* =======================================================
     Filtered rows
  ======================================================= */

  const filteredRows = useMemo(
    () => filterRows(rows, columns, search, filters),
    [rows, columns, search, filters],
  );

  /* =======================================================
     Sorted rows
  ======================================================= */

  const sortedRows = useMemo(
    () => sortRows(filteredRows, columns, sortConfig),
    [filteredRows, columns, sortConfig],
  );

  /* =======================================================
     Pagination
  ======================================================= */

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize));

  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  const visibleRows = useMemo(() => {
    const start = (page - 1) * pageSize;

    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, page, pageSize]);

  /* =======================================================
     Active filters
  ======================================================= */

  const activeFilterCount = Object.values(filters).filter((filter) =>
    Object.values(filter || {}).some((value) => value !== "" && value != null),
  ).length;

  /* =======================================================
     Database change
  ======================================================= */

  const changeDatabase = (nextDatabase) => {
    const nextTable = databaseTables[nextDatabase]?.[0] || null;

    setDatabase(nextDatabase);
    setTable(nextTable);

    resetView();
  };

  /* =======================================================
     Table change
  ======================================================= */

  const changeTable = (nextTableName) => {
    /*
      DatabaseSelector should normally
      return the table name.

      Convert it back into the
      complete table configuration.
    */

    const nextTable =
      tables.find((item) => item?.table === nextTableName) || null;

    setTable(nextTable);

    resetView();
  };

  /* =======================================================
     Reset view
  ======================================================= */

  function resetView() {
    setColumns([]);
    setRows([]);
    setFilters({});
    setSearch("");

    setSortConfig({
      key: "",
      direction: "asc",
    });

    setPage(1);
    setSelectedRow(null);
    setError("");
  }

  /* =======================================================
     Filter change
  ======================================================= */

  const changeFilter = (name, value) => {
    setFilters((current) => ({
      ...current,
      [name]: value,
    }));

    setPage(1);
  };

  /* =======================================================
     Clear filters
  ======================================================= */

  const clearFilters = () => {
    setFilters({});
    setSearch("");
    setPage(1);
  };

  /* =======================================================
     Sort
  ======================================================= */

  const handleSort = (column) => {
    const key = getColumnName(column);

    setSortConfig((current) => ({
      key,

      direction:
        current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));

    setPage(1);
  };

  /* =======================================================
     Render
  ======================================================= */

  return (
    <div className="container-fluid py-3 py-md-4 px-2 px-md-4">
      {/* =================================================
          Database selector
      ================================================= */}

      <DatabaseSelector
        databases={databases}
        database={database}
        table={table}
        tables={tables}
        onDatabaseChange={changeDatabase}
        onTableChange={changeTable}
        onLoad={() => fetchTable()}
        loading={loading}
        date={date}
        fetchTable={fetchTable}
        setDate={setDate}
      />

      {/* =================================================
          Error
      ================================================= */}

      {error && (
        <div className="alert alert-danger d-flex align-items-start gap-2">
          <FiAlertCircle className="mt-1" />

          <div className="flex-grow-1">{error}</div>

          <button
            className="btn btn-sm btn-outline-danger"
            onClick={() => setReloadKey((key) => key + 1)}
            type="button"
          >
            Retry
          </button>
        </div>
      )}

      {/* =================================================
          Loading
      ================================================= */}

      {loading && (
        <div className="card border-0 shadow-sm rounded-4 mb-4">
          <div className="card-body text-center py-5">
            <div className="spinner-border text-primary mb-3" />

            <div className="fw-semibold">
              Loading table structure and records...
            </div>
          </div>
        </div>
      )}

      {/* =================================================
          Data
      ================================================= */}

      {!loading && !error && columns.length > 0 && (
        <>
          {/* -------------------------------------------
                Statistics
            ------------------------------------------- */}

          <div className="row g-3 mb-4">
            {[
              {
                label: "Loaded rows",
                value: rows.length,
                color: "text-primary",
              },

              {
                label: "Columns",
                value: columns.length,
                color: "text-dark",
              },

              {
                label: "Matching rows",
                value: sortedRows.length,
                color: "text-success",
              },
            ].map((item) => (
              <div className="col-12 col-sm-4" key={item.label}>
                <div className="card border-0 shadow-sm rounded-4 h-100">
                  <div className="card-body">
                    <div className="small text-secondary mb-2">
                      {item.label}
                    </div>

                    <div className={`h3 fw-bold mb-0 ${item.color}`}>
                      {item.value.toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* -------------------------------------------
                Toolbar
            ------------------------------------------- */}

          <ExplorerToolbar
            database={database}
            /*
                IMPORTANT:
                Never pass/render the table
                configuration object as the
                actual table name.
              */

            table={table?.table || ""}
            search={search}
            onSearchChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            showFilters={showFilters}
            onToggleFilters={() => setShowFilters((current) => !current)}
            onClearFilters={clearFilters}
            onRefresh={() => setReloadKey((key) => key + 1)}
            loading={loading}
            activeFilterCount={activeFilterCount}
          />

          {/* -------------------------------------------
                Filters
            ------------------------------------------- */}

          {showFilters && (
            <DynamicFilters
              columns={columns}
              rows={rows}
              filters={filters}
              onFilterChange={changeFilter}
              onClear={clearFilters}
            />
          )}

          {/* -------------------------------------------
                Table
            ------------------------------------------- */}

          <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
            <div className="card-header bg-white py-3">
              <div className="fw-semibold">Table records</div>

              {/* IMPORTANT:
                    table is an object.
                    Render table.table.
                */}

              <div className="small text-secondary">
                {database}.{table?.table || ""}
              </div>
            </div>

            <DynamicDataTable
              columns={columns}
              rows={visibleRows}
              page={page}
              pageSize={pageSize}
              sortConfig={sortConfig}
              onSort={handleSort}
              onView={setSelectedRow}
            />

            <TablePagination
              page={page}
              pageSize={pageSize}
              pageSizeOptions={pageSizeOptions}
              totalRecords={sortedRows.length}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
            <DownloadExcelFromData
              data={sortedRows}
              columnOrder={columns.map((c) => c.Field)}
              fileName={` `}
            />
          </div>
        </>
      )}

      {/* =================================================
          Empty state
      ================================================= */}

      {!loading && !error && columns.length === 0 && (
        <div className="card border-0 shadow-sm rounded-4">
          <div className="card-body text-center py-5">
            <FiDatabase size={36} className="text-secondary mb-3" />

            <h5 className="fw-semibold">Select a table to begin</h5>

            <p className="text-secondary mb-0">
              The table structure and records will appear here.
            </p>
          </div>
        </div>
      )}

      {/* =================================================
          Row details
      ================================================= */}

      <RowDetailsModal
        row={selectedRow}
        columns={columns}
        onClose={() => setSelectedRow(null)}
      />
    </div>
  );
}
